import { useState, useEffect } from 'react';
import {
  Box, TextField, Button, Card, CardContent, Typography, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Paper, useMediaQuery, useTheme, Container, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, Grid
} from '@mui/material';
import { Search, Download, Visibility, Close } from '@mui/icons-material';


const ViewHistory = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `/api/collections?from=${dateRange.from}&to=${dateRange.to}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const data = await response.json();
      setCollections(data);
    } catch (error) {
      console.error('Failed to fetch collections:', error);
      // Demo data
      setCollections([
        { 
          id: 1, 
          date: '2025-11-18', 
          receiptNo: 'RCP001', 
          customerName: 'Rajesh Kumar', 
          customerId: 'CUST001',
          loanNumber: '123456789123456000',
          mobile: '9876543210',
          amount: 2500, 
          paymentMode: 'cash', 
          status: 'verified',
          chequeNo: '',
          transactionId: ''
        },
        { 
          id: 2, 
          date: '2025-11-18', 
          receiptNo: 'RCP002', 
          customerName: 'Priya Sharma', 
          customerId: 'CUST002',
          loanNumber: '123456789123456001',
          mobile: '9876543211',
          amount: 1800, 
          paymentMode: 'upi', 
          status: 'verified',
          chequeNo: '',
          transactionId: 'UPI2025111812345'
        },
        { 
          id: 3, 
          date: '2025-11-17', 
          receiptNo: 'RCP003', 
          customerName: 'Amit Patel', 
          customerId: 'CUST003',
          loanNumber: '123456789123456002',
          mobile: '9876543212',
          amount: 3200, 
          paymentMode: 'cash', 
          status: 'pending',
          chequeNo: '',
          transactionId: ''
        },
        { 
          id: 4, 
          date: '2025-11-17', 
          receiptNo: 'RCP004', 
          customerName: 'Sunita Reddy', 
          customerId: 'CUST004',
          loanNumber: '123456789123456003',
          mobile: '9876543213',
          amount: 950, 
          paymentMode: 'cheque', 
          status: 'verified',
          chequeNo: 'CHQ123456',
          transactionId: ''
        },
        { 
          id: 5, 
          date: '2025-11-16', 
          receiptNo: 'RCP005', 
          customerName: 'Vikram Singh', 
          customerId: 'CUST005',
          loanNumber: '123456789123456004',
          mobile: '9876543214',
          amount: 4200, 
          paymentMode: 'online', 
          status: 'verified',
          chequeNo: '',
          transactionId: 'TXN2025111612345'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Receipt No', 'Customer Name', 'Customer ID', 'Amount', 'Payment Mode', 'Status'];
    const csvData = collections.map(row => [
      new Date(row.date).toLocaleDateString('en-IN'),
      row.receiptNo,
      row.customerName,
      row.customerId,
      row.amount,
      row.paymentMode,
      row.status
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `collections_${dateRange.from}_to_${dateRange.to}.csv`;
    a.click();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (collection) => {
    setSelectedCollection(collection);
    setDetailsOpen(true);
  };

  const totalAmount = collections.reduce((sum, item) => sum + item.amount, 0);

  return (
    <Box sx={{ 
      bgcolor: '#f8f9fa', 
      minHeight: '100vh', 
      py: isMobile ? 1 : 3,
      px: isMobile ? 0 : 2
    }}>
      <Container 
        maxWidth="xl"
        disableGutters={isMobile}
        sx={{ px: isMobile ? 0 : 2 }}
      >
        {/* Header */}
        <Box sx={{ mb: isMobile ? 2 : 3, px: isMobile ? 2 : 0 }}>
          <Typography 
            variant={isMobile ? "h6" : "h4"} 
            sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}
          >
            Collection History
          </Typography>
          <Typography variant="caption" color="text.secondary">
            View and filter all payment collections
          </Typography>
        </Box>

        {/* Filter Card */}
        <Card 
          elevation={0}
          sx={{ 
            border: isMobile ? 'none' : '1px solid #e0e0e0',
            borderRadius: isMobile ? 0 : 2,
            mb: isMobile ? 0 : 2,
            borderBottom: '1px solid #e0e0e0'
          }}
        >
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Box sx={{ 
              display: 'flex', 
              gap: 1.5, 
              flexWrap: 'wrap',
              alignItems: 'flex-end'
            }}>
              <TextField
                label="From Date"
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{
                  flex: isMobile ? '1 1 45%' : '0 0 180px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5
                  }
                }}
              />
              <TextField
                label="To Date"
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{
                  flex: isMobile ? '1 1 45%' : '0 0 180px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5
                  }
                }}
              />
              <Button 
                variant="contained" 
                startIcon={<Search />}
                onClick={fetchCollections}
                size="small"
                sx={{
                  flex: isMobile ? '1 1 100%' : '0 0 auto',
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3
                }}
              >
                Search
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<Download />}
                onClick={exportToCSV}
                disabled={collections.length === 0}
                size="small"
                sx={{
                  flex: isMobile ? '1 1 100%' : '0 0 auto',
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3
                }}
              >
                Export
              </Button>
            </Box>

            {/* Summary Stats */}
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              mt: 3,
              flexWrap: 'wrap'
            }}>
              <Box sx={{ 
                flex: '1 1 auto',
                minWidth: isMobile ? '100%' : 'auto',
                bgcolor: '#e3f2fd',
                borderRadius: 1.5,
                p: 2,
                textAlign: 'center'
              }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Total Collections
                </Typography>
                <Typography variant="h6" color="primary.main" fontWeight={700}>
                  {collections.length}
                </Typography>
              </Box>
              <Box sx={{ 
                flex: '1 1 auto',
                minWidth: isMobile ? '100%' : 'auto',
                bgcolor: '#e8f5e9',
                borderRadius: 1.5,
                p: 2,
                textAlign: 'center'
              }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Total Amount
                </Typography>
                <Typography variant="h6" color="success.main" fontWeight={700}>
                  ₹{totalAmount.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Table Card */}
        <Card 
          elevation={0}
          sx={{ 
            border: isMobile ? 'none' : '1px solid #e0e0e0',
            borderRadius: isMobile ? 0 : 2
          }}
        >
          <CardContent sx={{ p: isMobile ? 0 : 3 }}>
            <TableContainer sx={{ 
              maxHeight: isMobile ? 'calc(100vh - 380px)' : 'none',
              overflowX: 'auto'
            }}>
              <Table size="small" stickyHeader={isMobile}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1.5 }}>
                      Date
                    </TableCell>
                    {!isMobile && (
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1.5 }}>
                        Receipt No
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1.5 }}>
                      Customer
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1.5 }}>
                      Amount
                    </TableCell>
                    {!isMobile && (
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1.5 }}>
                        Mode
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1.5 }}>
                      Status
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1.5 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 5 : 7} align="center" sx={{ py: 4 }}>
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : collections.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 5 : 7} align="center" sx={{ py: 4 }}>
                        No records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    collections
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ fontSize: '0.75rem', py: 1.5 }}>
                            {new Date(row.date).toLocaleDateString('en-IN', { 
                              day: '2-digit', 
                              month: 'short' 
                            })}
                          </TableCell>
                          {!isMobile && (
                            <TableCell sx={{ fontSize: '0.75rem', py: 1.5 }}>
                              {row.receiptNo}
                            </TableCell>
                          )}
                          <TableCell sx={{ fontSize: '0.75rem', py: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                              {row.customerName}
                            </Typography>
                            {isMobile && (
                              <Typography variant="caption" color="text.secondary">
                                {row.receiptNo}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 1.5 }}>
                            ₹{row.amount.toLocaleString()}
                          </TableCell>
                          {!isMobile && (
                            <TableCell sx={{ py: 1.5 }}>
                              <Chip 
                                label={row.paymentMode.toUpperCase()} 
                                size="small" 
                                sx={{
                                  bgcolor: row.paymentMode === 'cash' ? '#e8f5e9' : '#e3f2fd',
                                  color: row.paymentMode === 'cash' ? '#2e7d32' : '#1976d2',
                                  fontWeight: 600,
                                  fontSize: '0.65rem',
                                  height: 24
                                }}
                              />
                            </TableCell>
                          )}
                          <TableCell sx={{ py: 1.5 }}>
                            <Chip 
                              label={row.status} 
                              size="small" 
                              sx={{
                                bgcolor: row.status === 'verified' ? '#e8f5e9' : '#fff3e0',
                                color: row.status === 'verified' ? '#2e7d32' : '#f57c00',
                                fontWeight: 600,
                                fontSize: '0.65rem',
                                height: 24
                              }}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1.5 }}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleViewDetails(row)}
                              sx={{ 
                                bgcolor: 'primary.light',
                                '&:hover': { bgcolor: 'primary.main', color: 'white' }
                              }}
                            >
                              <Visibility sx={{ fontSize: 16 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={collections.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              sx={{
                borderTop: '1px solid #e0e0e0',
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  fontSize: '0.75rem'
                }
              }}
            />
          </CardContent>
        </Card>

        {/* Details Dialog */}
        <Dialog 
          open={detailsOpen} 
          onClose={() => setDetailsOpen(false)}
          fullScreen={isMobile}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            bgcolor: 'primary.main',
            color: 'white'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Collection Details
            </Typography>
            <IconButton onClick={() => setDetailsOpen(false)} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3, mt: 2 }}>
            {selectedCollection && (
              <Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Receipt Number
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {selectedCollection.receiptNo}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Customer Name
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedCollection.customerName}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Customer ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedCollection.customerId}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Mobile
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedCollection.mobile}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Date
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {new Date(selectedCollection.date).toLocaleDateString('en-IN')}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Amount
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                      ₹{selectedCollection.amount.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Payment Mode
                    </Typography>
                    <Chip 
                      label={selectedCollection.paymentMode.toUpperCase()} 
                      size="small" 
                      sx={{
                        bgcolor: selectedCollection.paymentMode === 'cash' ? '#e8f5e9' : '#e3f2fd',
                        color: selectedCollection.paymentMode === 'cash' ? '#2e7d32' : '#1976d2',
                        fontWeight: 600,
                        mt: 1
                      }}
                    />
                  </Grid>

                  {selectedCollection.chequeNo && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Cheque Number
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedCollection.chequeNo}
                      </Typography>
                    </Grid>
                  )}

                  {selectedCollection.transactionId && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Transaction ID
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedCollection.transactionId}
                      </Typography>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Chip 
                        label={selectedCollection.status.toUpperCase()} 
                        size="medium" 
                        sx={{
                          bgcolor: selectedCollection.status === 'verified' ? '#e8f5e9' : '#fff3e0',
                          color: selectedCollection.status === 'verified' ? '#2e7d32' : '#f57c00',
                          fontWeight: 700
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button 
              onClick={() => setDetailsOpen(false)} 
              variant="contained"
              fullWidth
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default ViewHistory;
